import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { PosTicketRequest, OrderResponseRecord, ReciboMotivo } from '../models/api.models';
import { environment } from '../../environments/environment';
import { SessionService } from '../core/tenant/session.service';
import { LEYENDA_NO_ES_FACTURA } from '../core/impresion/documentos-imprimibles';

/**
 * Leyenda obligatoria de la prefactura (B-08).
 *
 * Un ticket sin resolución DIAN no es una factura de venta, pero salía con el
 * mismo aspecto que una. Quien lo recibe no tiene cómo distinguirlo.
 */
// Contador §2: el texto de la leyenda vive con la tirilla (core/impresion); se reexporta para quien lo usaba de aquí.
export const LEYENDA_PREFACTURA = LEYENDA_NO_ES_FACTURA;

/** Lo que el agente sabe de la impresora. El agente viejo solo distingue `no_encontrada`. */
export type EstadoImpresora = 'lista' | 'sin_conexion' | 'en_cola' | 'error' | 'desconocida' | 'no_encontrada';

/** Estado del agente local, con nombre honesto (docs/arquitectura/21 §2.4). */
export interface EstadoAgente {
    /** El agente respondió (sea lo que sea que diga de la impresora). */
    conectado: boolean;
    /** Si no respondió: por qué. */
    motivo?: ReciboMotivo;
    impresora?: EstadoImpresora;
    enCola?: number;
    detalle?: string;
    version?: string;
    /** `performance.now()` de la última consulta. */
    consultadoEn: number;
}

/** Resultado de mandar algo al agente. `enviado` es lo máximo que afirma el agente viejo. */
export interface ResultadoImpresion {
    estado: 'enviado' | 'confirmado' | 'no_impreso';
    motivo?: ReciboMotivo;
    detalle?: string;
}

/** Fallo clasificado. `motivo` es lo que el cajero necesita para saber qué hacer. */
export class ErrorDeImpresion extends Error {
    constructor(readonly motivo: ReciboMotivo, readonly detalle: string) {
        super(detalle);
        this.name = 'ErrorDeImpresion';
    }
}

/**
 * Adaptador del agente local de impresión (`ms-print-agent`, `localhost:8181`).
 *
 * Las pantallas NO hablan con esto: hablan con `ImpresionService`, que es el
 * único punto de entrada para imprimir y el que mañana podría cambiar de vía
 * (p. ej. `window.print()`) sin tocar ninguna pantalla.
 *
 * <h2>Compatibilidad con el agente viejo</h2>
 *
 * El agente que hay instalado responde texto: `ONLINE`/`OFFLINE` en `/status`
 * y `Ticket Impreso` en `/ticket`. Esa es la vía principal. Si un agente nuevo
 * responde JSON (`{agente, impresora:{estado,…}}` o `{resultado}`), también se
 * entiende, pero nada depende de ello.
 *
 * <h2>Lo que puede afirmar</h2>
 *
 * Un 200 del agente significa que el spooler aceptó los bytes, no que salió
 * papel. Por eso el resultado normal es `enviado`, nunca «impreso».
 */
@Injectable({
    providedIn: 'root'
})
export class PrinterService {
    private http = inject(HttpClient);
    private session = inject(SessionService);
    // Hardware: SIEMPRE local (agente en la máquina del cliente), no cloud.
    private apiUrl = `${environment.agentBaseUrl}/api/printer`;

    /**
     * Topes de espera (docs/arquitectura/21 §3). Un agente local sano responde
     * en milisegundos; el tiempo solo se gasta cuando Chrome espera el globo de
     * permiso o el agente está colgado, y ahí 5 s ya es «está roto».
     */
    static readonly TIMEOUT_STATUS_MS = 2000;
    static readonly TIMEOUT_IMPRESION_MS = 5000;
    /**
     * Por debajo de esto, un fallo es «conexión rechazada»: el agente no está
     * corriendo. Por encima, algo respondió tarde o no respondió.
     */
    static readonly UMBRAL_RECHAZO_MS = 300;
    /** Máximo de fallos seguidos del sondeo AUTOMÁTICO antes de dejar de sondear. */
    static readonly MAX_INTENTOS = 5;

    /** Fallos consecutivos de conexión con el agente (sondeo y clics por igual). */
    readonly fallosConsecutivos = signal(0);
    /**
     * `true` cuando se agotaron los reintentos automáticos. Frena SOLO el
     * sondeo automático (`sondear()`): un clic humano —cobrar, reimprimir,
     * probar— siempre intenta una vez, porque el cajero que pulsa no puede
     * recibir un fallo instantáneo de una impresora que ya volvió.
     */
    readonly offline = computed(() => this.fallosConsecutivos() >= PrinterService.MAX_INTENTOS);

    /** Último estado conocido del agente. */
    readonly estado = signal<EstadoAgente | null>(null);

    /** Reinicia la búsqueda de impresora. */
    reiniciarBusqueda(): void {
        this.fallosConsecutivos.set(0);
    }

    /**
     * Sondeo automático: respeta el corte por fallos. Devuelve el estado
     * (el último conocido si no sondeó).
     */
    async sondear(): Promise<EstadoAgente | null> {
        if (this.offline()) {
            return this.estado();
        }
        return this.consultarEstado();
    }

    /** Consulta el estado del agente. Siempre intenta (clic humano). */
    async consultarEstado(): Promise<EstadoAgente> {
        const inicio = performance.now();
        try {
            const res = await this.conTimeout(
                this.http.get(`${this.apiUrl}/status`, { responseType: 'text' }),
                PrinterService.TIMEOUT_STATUS_MS);
            this.fallosConsecutivos.set(0);
            const estado = PrinterService.interpretarEstado(res, inicio);
            this.estado.set(estado);
            return estado;
        } catch (e) {
            const estado = await this.interpretarFalloDeEstado(e, inicio);
            this.estado.set(estado);
            return estado;
        }
    }

    /** `ONLINE` / `OFFLINE` del agente viejo, o el JSON del nuevo. */
    static interpretarEstado(cuerpo: string, consultadoEn: number): EstadoAgente {
        const texto = (cuerpo ?? '').trim();
        if (texto.startsWith('{')) {
            try {
                const json = JSON.parse(texto);
                const imp = json?.impresora ?? {};
                const encontrada = imp.encontrada !== false;
                const estado = PrinterService.estadoImpresoraDe(imp.estado);
                return {
                    conectado: true,
                    impresora: encontrada ? estado : 'no_encontrada',
                    enCola: typeof imp.enCola === 'number' ? imp.enCola : undefined,
                    detalle: typeof imp.detalle === 'string' && imp.detalle ? imp.detalle : undefined,
                    version: typeof json?.version === 'string' ? json.version : undefined,
                    consultadoEn,
                };
            } catch {
                /* JSON roto: se trata como texto */
            }
        }
        // Agente viejo: ONLINE = «existe una cola con ese nombre». No dice nada
        // del papel, así que la impresora queda `desconocida`, nunca `lista`.
        return { conectado: true, impresora: 'desconocida', consultadoEn };
    }

    private static estadoImpresoraDe(valor: unknown): EstadoImpresora {
        switch (valor) {
            case 'lista': case 'sin_conexion': case 'en_cola': case 'error':
                return valor;
            default:
                return 'desconocida';
        }
    }

    private async interpretarFalloDeEstado(e: unknown, inicio: number): Promise<EstadoAgente> {
        // 503 OFFLINE del agente viejo: el agente responde, la impresora no está.
        if (e instanceof HttpErrorResponse && e.status === 503) {
            this.fallosConsecutivos.set(0);
            return { conectado: true, impresora: 'no_encontrada', consultadoEn: inicio };
        }
        this.fallosConsecutivos.update((n) => n + 1);
        const err = await this.clasificar(e, inicio);
        return { conectado: false, motivo: err.motivo, detalle: err.detalle, consultadoEn: inicio };
    }

    /**
     * Clasifica un fallo del agente (docs/arquitectura/21 §1):
     *  - rechazado en < 300 ms → el agente no está corriendo;
     *  - timeout con Chrome pidiendo permiso → el globo de «red local»;
     *  - timeout sin más → el agente no responde;
     *  - 5xx → el agente lo intentó y falló, con su texto.
     */
    async clasificar(e: unknown, inicio: number): Promise<ErrorDeImpresion> {
        const transcurrido = performance.now() - inicio;
        if (e instanceof HttpErrorResponse && e.status >= 500 && e.status !== 503) {
            const cuerpo = typeof e.error === 'string' ? e.error : (e.error?.detalle ?? e.error?.message ?? '');
            return new ErrorDeImpresion('error_agente', cuerpo || `El programa de impresión respondió ${e.status}`);
        }
        if (e instanceof HttpErrorResponse && e.status === 503) {
            return new ErrorDeImpresion('error_agente', 'El programa de impresión no encuentra la impresora');
        }
        const esTimeout = e instanceof TimeoutError
            || transcurrido >= Math.min(PrinterService.TIMEOUT_STATUS_MS, PrinterService.TIMEOUT_IMPRESION_MS) - 50;
        if (!esTimeout && transcurrido < PrinterService.UMBRAL_RECHAZO_MS) {
            return new ErrorDeImpresion('agente_apagado',
                'El programa de impresión no está abierto en este computador');
        }
        if (await this.chromePidePermiso()) {
            return new ErrorDeImpresion('navegador_pide_permiso',
                'Chrome está pidiendo permiso arriba: pulsa Permitir');
        }
        return new ErrorDeImpresion('agente_no_responde', 'El programa de impresión no responde');
    }

    /** Chrome 142+ cuelga la llamada a `localhost` hasta que alguien conteste el globo. */
    async chromePidePermiso(): Promise<boolean> {
        try {
            const perms = (navigator as unknown as { permissions?: Permissions }).permissions;
            if (!perms?.query) return false;
            const p = await perms.query({ name: 'local-network-access' as PermissionName });
            return p.state === 'prompt';
        } catch {
            return false;
        }
    }

    /**
     * Manda una petición al agente. Siempre intenta (es un clic humano o el
     * cobro): el corte por fallos no aplica aquí.
     */
    private async enviar(ruta: string, cuerpo: unknown): Promise<ResultadoImpresion> {
        const inicio = performance.now();
        try {
            const res = await this.conTimeout(
                this.http.post(`${this.apiUrl}${ruta}`, cuerpo, { responseType: 'text' }),
                PrinterService.TIMEOUT_IMPRESION_MS);
            this.fallosConsecutivos.set(0);
            return PrinterService.interpretarResultado(res);
        } catch (e) {
            const err = await this.clasificar(e, inicio);
            if (err.motivo !== 'error_agente') {
                this.fallosConsecutivos.update((n) => n + 1);
            }
            throw err;
        }
    }

    /** `Ticket Impreso` (texto, agente viejo) → `enviado`; JSON `{resultado}` del nuevo. */
    static interpretarResultado(cuerpo: string): ResultadoImpresion {
        const texto = (cuerpo ?? '').trim();
        if (texto.startsWith('{')) {
            try {
                const json = JSON.parse(texto);
                const detalle = typeof json?.detalle === 'string' && json.detalle ? json.detalle : undefined;
                switch (json?.resultado) {
                    case 'confirmado': return { estado: 'confirmado', detalle };
                    case 'en_cola': return { estado: 'no_impreso', motivo: 'en_cola', detalle };
                    case 'error': return { estado: 'no_impreso', motivo: 'error_agente', detalle };
                    default: return { estado: 'enviado', detalle };
                }
            } catch {
                /* JSON roto: se trata como texto */
            }
        }
        // Un 200 del agente viejo: el spooler aceptó los bytes. Nada más.
        return { estado: 'enviado' };
    }

    private conTimeout<T>(obs: Parameters<typeof firstValueFrom<T>>[0], ms: number): Promise<T> {
        return firstValueFrom(obs.pipe(timeout(ms)) as typeof obs);
    }

    async printTicket(request: PosTicketRequest): Promise<ResultadoImpresion> {
        return this.enviar('/ticket', request);
    }

    async printTicketBatch(requests: PosTicketRequest[]): Promise<ResultadoImpresion> {
        return this.enviar('/ticket-batch', requests);
    }

    async printOrderTicketEmergency(order: OrderResponseRecord): Promise<ResultadoImpresion> {
        return this.enviar('/order-ticket', order);
    }

    async openDrawer(): Promise<ResultadoImpresion> {
        return this.enviar('/drawer/open', {});
    }

    /** Compatibilidad: el texto crudo de `/status` (`ONLINE`/`OFFLINE`). Prefiere `consultarEstado()`. */
    async getStatus(): Promise<string> {
        const estado = await this.consultarEstado();
        if (!estado.conectado) {
            throw new ErrorDeImpresion(estado.motivo ?? 'agente_no_responde', estado.detalle ?? '');
        }
        return estado.impresora === 'no_encontrada' ? 'OFFLINE' : 'ONLINE';
    }

    /** `pie`: M34 A7, el de la plantilla (`comprobante.pie`, más reciente que la sesión). Sin él, el de la sesión. */
    buildTicketRequest(order: OrderResponseRecord, isInvoice: boolean = false, cashGiven?: number, change?: number, pie?: string | null): PosTicketRequest {
        // Datos del negocio desde la sesión (del backend, por tenant) — ya NO hardcodeados.
        const biz = this.session.session();
        const req: PosTicketRequest = {
            businessName: biz?.tenantName || 'NEGOCIO',
            nit: biz?.nit || '',
            address: biz?.address || '',
            phone: biz?.phone || '',
            ticketNumber: `INV-${(order.idOrder ?? 0).toString().padStart(5, '0')}`,
            cashierName: biz?.userName || '',
            dateTime: order.createdAt,
            // Órdenes de la app móvil pueden no traer items cargados/subtotal: evitar el crash
            // que impedía imprimir la factura.
            items: (order.items || []).map(i => ({
                name: i.nameProduct,
                quantity: i.quantity,
                unitPrice: i.precioPorKilo ?? i.unitPrice,
                total: i.totalPrice,
                ...(i.precioPorKilo != null ? { porPeso: true } : {})
            })),
            subtotal: order.subtotal ?? order.total,
            tax: 0,
            total: order.total,
            paymentMethod: order.paymentMethod,
            cashGiven: cashGiven || 0,
            change: change || 0,
            footerMessage: (pie !== undefined ? pie : biz?.ticketFooter) || "¡Gracias por su compra!",
            abrirCajon: true,
        };

        // NINGUNA tirilla lleva resolución de la DIAN, ni cuando el cliente pide
        // factura electrónica. Hasta el 2026-09-11 esta rama imprimía un número de
        // resolución, un rango, una cédula y un QR INVENTADOS («Res. DIAN
        // 18762025001…», «222222222222», `documentkey=xxx-`): un documento que
        // aparenta ser tributario sin serlo, en manos de un cliente y con el nombre
        // del comerciante encima. SureSell no emite todavía factura electrónica: no
        // hay proveedor ni resolución, así que no hay nada verdadero que imprimir.
        //
        // Cuando el cajero marca «Requiere factura electrónica», sus datos
        // (`customerName`, `customerId`) los pone quien llama, con lo que tecleó el
        // cliente; aquí no se inventa ninguno.
        //
        // B-08 — La leyenda va SIEMPRE, y al inicio del pie porque es lo único que
        // el agente de impresión imprime siempre; un campo nuevo lo ignoraría.
        req.footerMessage = [LEYENDA_PREFACTURA, req.footerMessage]
            .filter(t => t && t.trim().length > 0)
            .join('\n');
        if (isInvoice) {
            // Lo único que cambia: se dice que el cliente la pidió (Producto, 04/10). No se promete un envío: sin el
            // módulo, o con la facturación inactiva, no se envía nada.
            req.footerMessage = [req.footerMessage, 'Factura electrónica solicitada.'].join('\n');
        }

        return req;
    }
}
